import { Collapse } from '@mui/material';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { HiArrowLeft } from 'react-icons/hi';
import { useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ViewOffer from 'src/components/my-applications/offer/ViewOffer';
import MyOpportunitiesDriveCard from 'src/components/OfflineDriveUserAttendance/OfflineDriveMyOpportunities/MyOpportunitiesDriveCard';
import OpportunitiesDriveHeader from 'src/components/OfflineDriveUserAttendance/OfflineDriveMyOpportunities/OpportunitiesDriveHeader';
import NoRecruiterPositionsContainer from 'src/components/recruiterPositions/NoRecruiterPositionsContainer';
import Container from 'src/components/shared/Container';
import { useUserContext } from 'src/context/UserProvider';
import { useGetDriveApplicationsForUser, useGetMyRegisteredDrives } from 'src/hooks/offlineDrive.hooks';
import { OfflineDriveCandidateStatus, UIApplication } from 'types';

export function statusIcon(status: OfflineDriveCandidateStatus) {
	let bgColor;
	switch (status) {
		case OfflineDriveCandidateStatus.ATTENDANCE_NOT_MARKED:
			bgColor = 'bg-yellow-100 text-yellow-400';
			break;
		case OfflineDriveCandidateStatus.BUSY:
			bgColor = 'bg-red-100 text-red-500';
			break;
		case OfflineDriveCandidateStatus.ASSIGN:
			bgColor = 'bg-blue-100 text-blue-500';
			break;
		case OfflineDriveCandidateStatus.HIRED:
			bgColor = 'bg-green-100 text-green-500';
			break;
		case OfflineDriveCandidateStatus.IDLE:
			bgColor = 'bg-green-100 text-[#39D98A]';
			break;
		case OfflineDriveCandidateStatus.OUT_OF_DRIVE:
			bgColor = 'bg-slate-100 text-slate-600';
			break;
		default:
			bgColor = 'bg-green-100 text-[#39D98A]';
			break;
	}

	const displayLabel =
		status === OfflineDriveCandidateStatus.OUT_OF_DRIVE ? 'out of drive' : String(status).replace(/_/g, ' ');

	return <div className={`rounded-md capitalize px-3 py-1 border font-[500] ${bgColor}`}>{displayLabel}</div>;
}

const OfflineDriveDayOpportunitiesPage: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { selectedDrive, setSelectedDrive, user } = useUserContext();
	const queryClient = useQueryClient();
	useEffect(() => {
		queryClient.refetchQueries({ queryKey: ['verifyCookie'] });
	}, [queryClient]);

	useEffect(() => {
		if (selectedDrive) {
			searchParams.set('driveId', selectedDrive?.value || '');
			setSearchParams(searchParams);
		} else if (searchParams.has('driveId')) {
			setSelectedDrive({ label: searchParams.get('driveId') || '', value: searchParams.get('driveId') || '' });
		}
	}, [selectedDrive, searchParams, setSearchParams, setSelectedDrive]);

	const hasDriveIdInUrl = searchParams.has('driveId') && !!searchParams.get('driveId');

	const { data: myRegisteredDrives, isLoading: isMyRegisteredDrivesLoading } = useGetMyRegisteredDrives({
		enabled: !selectedDrive?.value && !hasDriveIdInUrl,
		onComplete(data) {
			if (data?.latestDriveId) {
				setSelectedDrive({ label: '', value: data.latestDriveId });
			}
		},
	});

	const [selectedApplication, setSelectedApplication] = useState<UIApplication | null>(null);

	const { data: driveApplications } = useGetDriveApplicationsForUser({
		driveId: selectedDrive?.value || '',
	});

	const viewOfferId = searchParams.get('viewOffer');
	useEffect(() => {
		if (!viewOfferId || !driveApplications?.applications?.length) return;
		const match = driveApplications.applications.find(a => String(a._id) === String(viewOfferId));
		if (match) {
			setSelectedApplication(match as UIApplication);
		}
	}, [viewOfferId, driveApplications?.applications]);

	return (
		<>
			<OpportunitiesDriveHeader />
			<Container className={classNames('h-full flex-1 flex flex-col !pt-20 !pb-4')}>
				<div className="flex justify-between border-b border-[#E4E4EB] mb-4 pb-2 md:pb-[10px]">
					<div className="flex items-center gap-2 text-xl font-[600] text-[#1C1C28] md:text-[24px] md:font-[700]">
						<Collapse orientation="horizontal" in={!!(searchParams.has('viewOffer') && selectedApplication)}>
							<button
								className="my-auto h-full flex items-center"
								onClick={() => {
									searchParams.delete('viewOffer');
									setSearchParams(searchParams);
									setSelectedApplication(null);
								}}
							>
								<HiArrowLeft size={24} color="#1C1C28" />
							</button>
						</Collapse>
						<span>My opportunties</span>
					</div>

					{driveApplications?.userDriveDetails?.offlineDriveCandidateStatus ? (
						<div className="flex items-center gap-2">
							<span className="text-[#666666] text-[14px] font-[500]">Your Status:</span>
							{statusIcon(driveApplications?.userDriveDetails?.offlineDriveCandidateStatus)}
						</div>
					) : null}
				</div>

				{isMyRegisteredDrivesLoading ? (
					<div className="my-auto flex flex-col items-center justify-center max-w-[516px] mx-auto">
						<div className="flex align-center justify-center">
							<NoRecruiterPositionsContainer
								showAction={false}
								title="Loading drive details..."
								description="Please wait while we load your latest offline drive."
							/>
						</div>
					</div>
				) : !selectedDrive?.value && !hasDriveIdInUrl && myRegisteredDrives && myRegisteredDrives.drives?.length === 0 ? (
					<div className="my-auto flex flex-col items-center justify-center max-w-[516px] mx-auto">
						<div className="flex align-center justify-center">
							<NoRecruiterPositionsContainer
								showAction={false}
								title="No offline drives assigned"
								description="If you were invited to an offline drive, it will show up here once you're registered."
							/>
						</div>
					</div>
				) : searchParams.has('viewOffer') && selectedApplication ? (
					<div className="w-full -mt-4">
						<ViewOffer
							application={selectedApplication as UIApplication}
							onFinish={() => {
								searchParams.delete('viewOffer');
								setSearchParams(searchParams);
								navigate(`/offline-drive-day-opportunities?driveId=${selectedDrive?.value}`);
							}}
						/>
					</div>
				) : driveApplications?.applications.length ? (
					<div className="flex flex-col max-w-full md:max-w-[760px] gap-[20px]">
						{driveApplications?.applications.map((application, index) => (
							<MyOpportunitiesDriveCard
								key={index}
								application={application as UIApplication}
								setSelectedApplication={setSelectedApplication}
							/>
						))}
					</div>
				) : (
					<div className="my-auto flex flex-col items-center justify-center max-w-[516px] mx-auto">
						<div className="flex align-center justify-center">
							<NoRecruiterPositionsContainer
								showAction={false}
								title="No opportunities assigned"
								description="Keep checking this space for assigned job opportunities. We’re working on matching you with the right roles!"
							/>
						</div>
					</div>
				)}
			</Container>
		</>
	);
};

export default OfflineDriveDayOpportunitiesPage;
